import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, TrashIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { invoiceTemplateService } from '../../api/services';
import { InvoiceTemplate } from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

const InvoiceTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setTemplates(await invoiceTemplateService.getAll());
    } catch {
      toast.error('Impossible de charger les modèles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSetDefault = async (t: InvoiceTemplate) => {
    if (t.isDefault) return;
    try {
      setBusyId(t.id);
      await invoiceTemplateService.setDefault(t.id);
      await load();
      toast.success(`« ${t.name} » défini par défaut`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (t: InvoiceTemplate) => {
    if (t.isDefault) {
      toast.error('Définissez d\'abord un autre modèle par défaut');
      return;
    }
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try {
      setBusyId(t.id);
      await invoiceTemplateService.delete(t.id);
      await load();
      toast.success('Modèle supprimé');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Modèles de facture</h2>
          <p className="text-sm text-gray-500">
            Personnalisez la couleur, le pied de page, les conditions de paiement
            et les mentions légales appliqués à vos PDFs.
          </p>
        </div>
        <Link
          to="/parametres/modeles/new"
          className="btn-primary inline-flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Nouveau modèle
        </Link>
      </div>

      {templates.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          Aucun modèle. Cliquez sur « Nouveau modèle » pour commencer.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <div key={t.id} className="card p-4 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium truncate" title={t.name}>{t.name}</h3>
                    {t.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        <CheckBadgeIcon className="h-3 w-3 mr-1" />
                        Par défaut
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <span
                  className="inline-block h-5 w-5 rounded border border-gray-300"
                  style={{ backgroundColor: t.primaryColor }}
                  title={t.primaryColor}
                />
                <span className="font-mono text-xs">{t.primaryColor}</span>
              </div>

              {(t.footerText || t.paymentTerms || t.legalMentions) && (
                <div className="mt-3 text-xs text-gray-500 line-clamp-3">
                  {t.paymentTerms || t.footerText || t.legalMentions}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/parametres/modeles/${t.id}/edit`)}
                  className="text-gray-700 hover:text-primary-700 p-1"
                  title="Modifier"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                {!t.isDefault && (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(t)}
                    disabled={busyId === t.id}
                    className="text-xs text-primary-700 hover:underline disabled:opacity-50"
                  >
                    Définir par défaut
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(t)}
                  disabled={t.isDefault || busyId === t.id}
                  className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-30 p-1"
                  title={t.isDefault ? 'Modèle par défaut — impossible à supprimer' : 'Supprimer'}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplates;
